import { Outlet, Link, useLocation } from "react-router-dom";
import { Radar, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useState } from "react";

const navLinks = [
  { key: "navPricing", to: "/pricing" },
  { key: "navAbout", to: "/about" },
] as const;

export function MarketingLayout() {
  const { t } = useTranslation("common");
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Navbar */}
      <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-sm">
        <div className="container flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 no-underline">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
              <Radar className="h-5 w-5 text-primary" />
            </div>
            <span className="text-lg font-bold tracking-tight text-foreground">
              FirmenFinden
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-8 md:flex">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === link.to
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              >
                {t(`marketing.${link.key}`)}
              </Link>
            ))}
            <LanguageSwitcher />
            <Button asChild size="sm">
              <Link to="/signup">{t("marketing.ctaGetStarted")}</Link>
            </Button>
          </nav>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 text-muted-foreground hover:text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={t("marketing.menuLabel")}
          >
            {mobileOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="border-t bg-background px-4 pb-4 pt-2 md:hidden">
            <nav className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === link.to
                      ? "text-primary"
                      : "text-muted-foreground"
                  }`}
                >
                  {t(`marketing.${link.key}`)}
                </Link>
              ))}
              <div className="flex items-center gap-2">
                <LanguageSwitcher />
              </div>
              <Button asChild size="sm" className="w-fit">
                <Link to="/signup" onClick={() => setMobileOpen(false)}>
                  {t("marketing.ctaGetStarted")}
                </Link>
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Page content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t bg-muted/30">
        <div className="container flex flex-col items-center gap-4 py-8 text-center md:flex-row md:justify-between md:text-left">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Radar className="h-4 w-4 text-primary" />
            <span>{t("marketing.footerBy")}</span>
          </div>
          <nav className="flex gap-6 text-sm text-muted-foreground">
            <Link
              to="/pricing"
              className="hover:text-foreground transition-colors"
            >
              {t("marketing.navPricing")}
            </Link>
            <Link
              to="/about"
              className="hover:text-foreground transition-colors"
            >
              {t("marketing.navAbout")}
            </Link>
            <Link
              to="/impressum"
              className="hover:text-foreground transition-colors"
            >
              {t("marketing.navImpressum")}
            </Link>
          </nav>
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} 4T Software.{" "}
            {t("marketing.footerRights")}
          </p>
        </div>
      </footer>
    </div>
  );
}
